import {Component, OnInit, Input, ChangeDetectionStrategy, ChangeDetectorRef} from '@angular/core';
import {User} from 'app/core/domain/user';
import {Location} from '@angular/common';
import {ActivatedRoute} from '@angular/router';
import {Role} from 'app/core/domain/role';
import {UserService} from 'app/core/service/user.service';
import {RoleService} from 'app/core/service/role.service';
import {SelectItem} from 'primeng/components/common/api';
import {GlobalAppService} from '../../../core/commons/global-app.service';
import {MessageSeverity} from '../../../core/commons/message-severity';
import {FxService} from '../../../core/service/fx.service';
import {FormBuilder, FormGroup, Validators} from '@angular/forms';
import {FX} from '../../../core/domain/fx';
import {TSet} from '../../../core/commons/utils/set';
import {ValidationService} from '../../../core/validation-messages/validation.service';

@Component({
    selector: 'app-user-form',
    templateUrl: './user-form.component.html',
    styleUrls: ['./user-form.component.scss'],
    providers: [UserService, RoleService, FxService],
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class UserFormComponent implements OnInit {

    user: User;

    title = '';

    userFormGroup: FormGroup;

    rolesSelectItems: SelectItem[];

    roles: Array<Role>;

    previousRoles: Array<number> = [];

    isNew = false;

    constructor(private fb: FormBuilder,
                private route: ActivatedRoute,
                private location: Location,
                private userService: UserService,
                private roleService: RoleService,
                private appService: GlobalAppService,
                private validationService: ValidationService,
                private cd: ChangeDetectorRef) {

        this.userFormGroup = this.fb.group({
            firstname: ['', [Validators.required, Validators.minLength(1), Validators.maxLength(50)]],
            lastname: ['', [Validators.required, Validators.minLength(1), Validators.maxLength(50)]],
            username: ['', [Validators.required, Validators.minLength(4), Validators.maxLength(50)]],
            email: ['', [Validators.required, Validators.email]],
            roles: []
        });
    }

    ngOnInit() {
        const self = this;
        this.appService.setLoading(true);
        this.roleService.getAll()
            // .do(roles => console.log(roles))
            .subscribe(roles => {
                this.roles = roles;
                this.rolesSelectItems = roles.map(role => ({label: role.name, value: role.id}));

                const id = this.route.params
                    .map(params => params['id']);

                id.filter(i => i !== 'new')
                    .do(i => this.isNew = false)
                    .switchMap(i => this.userService.get(+i))
                    .do(user => this.user = user)
                    .subscribe(user => {

                        this.title = 'Editar un usuario';
                        const rolesId =  user.roles.map(role => role.id);
                        this.userFormGroup.setValue({
                            firstname: user.firstname,
                            lastname: user.lastname,
                            username: user.username,
                            email: user.email,
                            roles: rolesId
                        });
                        this.previousRoles = rolesId.slice();
                        this.appService.setLoading(false);
                        this.cd.markForCheck();
                    });

                id.filter(id => id === 'new')
                    .do(id => this.isNew = true)
                    .subscribe(id => {
                        this.title = 'Nuevo usuario';
                        this.user = new User();
                        this.user.fxs = [];
                        this.user.roles = [];
                        this.appService.setLoading(false);
                        this.cd.markForCheck();
                    });
            });
    }

    onRolesChange(event) {
        this.updateRoles(this.userFormGroup.getRawValue().roles)
    }

    updateRoles(roles: number[], updateFxs = true) {
        const self = this;
        if (updateFxs) {
            const fxSet = new TSet<FX>(this.user.fxs);
            this.previousRoles
                .map(roleId => self.getRoleById(roleId).fxs)
                .forEach(fxs => fxSet.removeAll(fxs));
            roles
                .map(roleId => this.getRoleById(roleId).fxs)
                .forEach(fxs => fxSet.addAll(fxs));
            this.user.fxs = fxSet.toArray();
        }
        this.roleService.findByFxs(this.user.fxs)
            .map(r => r.map(role => role.id))
            // .do(r => console.log(r))
            // .do(rolesId => self.previousRoles = rolesId)
            .subscribe(function(rolesId) {
                self.userFormGroup.patchValue({roles : rolesId});
                self.cd.markForCheck();
            });
    }

    updateFxs(fxs: FX[]) {
        console.log('updateFxs called');
        const self = this;
        this.roleService.findByFxs(fxs)
            .map(r => r.map(role => role.id))
            // .do(rolesId => self.previousRoles = rolesId)
            .subscribe(function(rolesId) {
                self.userFormGroup.patchValue({roles : rolesId});
                self.updateRoles(rolesId, false);
                self.cd.markForCheck();
            });
        this.user.fxs = fxs;
    }

    getRoleById(id: number) {
        return this.roles.find(role => role.id === id);
    }

    formToEntity(form: FormGroup): User {
        const formValue = form.value;
        const user = new User({
            id: this.user.id,
            firstname: formValue.firstname,
            lastname: formValue.lastname,
            username: formValue.username,
            email: formValue.email,
            fxs: this.user.fxs
        });
        return user;
    }

    onSubmit(event) {
        // event.preventDefault();
        this.appService.setLoading(true);
        this.user = this.formToEntity(this.userFormGroup);
        console.log('isNew');
        if (!this.isNew) {
            this.userService.update(this.user).subscribe(
                user => {
                    this.appService.sendMsg(
                        MessageSeverity.SUCCESS,
                        'Guardado',
                        'Se ha guardado el usuario correctamente'
                    );
                    this.appService.setLoading(false);
                    this.goBack();
                },
                error => {
                    this.appService.sendMsg(
                        MessageSeverity.ERROR,
                        'Error: ' + error.json().error,
                        'No se a podido guardar el usuario. ' + error.json().message
                    );
                    this.appService.setLoading(false);
                }
            );
        } else {
            this.userService.create(this.user).subscribe(
                user => {
                    this.appService.sendMsg(
                        MessageSeverity.SUCCESS,
                        'Guardado',
                        'Se ha creado el usuario correctamente'
                    );
                    this.appService.setLoading(false);
                    this.goBack();
                },
                error => {
                    this.appService.sendMsg(
                        MessageSeverity.ERROR,
                        'Error: ' + error.json().error,
                        'No se a podido crear el usuario. ' + error.json().message
                    );
                    this.appService.setLoading(false);
                }
            );
        }
        event.preventDefault();
    }

    goBack() {
        this.location.back();
    }
}



// WEBPACK FOOTER //
// ./src/app/admin/users/user-form/user-form.component.ts